import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import DarkModeRoundedIcon from "@mui/icons-material/DarkModeRounded";
import LightModeRoundedIcon from "@mui/icons-material/LightModeRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import SmartToyRoundedIcon from "@mui/icons-material/SmartToyRounded";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  BaseBox,
  BaseButton,
  BasePageLayout,
  BasePaper,
  BaseTextField,
  BaseTypography,
} from "../../Common/UI/exports";
import { sendAIAgentMessage } from "../../APIs/AIAgentAPI";

const initialMessages = [
  {
    role: "assistant",
    content:
      "Hi! \nI'm your AI assistant. How Can I Help You?",
  },
];

const createChatSession = (id) => ({
  id,
  title: "New chat",
  messages: initialMessages,
  attachments: [],
  isSending: false,
  error: "",
});

const themeModes = {
  teams: {
    pageBackground: "linear-gradient(180deg, #1f2128 0%, #17191f 46%, #14161b 100%)",
    sidebarBackground: "linear-gradient(180deg, #2a2d38 0%, #232632 100%)",
    sidebarBorderRight: "1px solid rgba(255,255,255,0.08)",
    collapseButtonBackground: "rgba(255,255,255,0.04)",
    collapseButtonBorder: "1px solid rgba(255,255,255,0.12)",
    sectionTextColor: "rgba(255,255,255,0.62)",
    collapsedTextColor: "rgba(255,255,255,0.58)",
    newChatButtonBackground: "rgba(98, 100, 167, 0.18)",
    newChatButtonBorder: "rgba(133, 135, 214, 0.36)",
    newChatButtonHoverBackground: "rgba(98, 100, 167, 0.28)",
    newChatButtonHoverBorder: "rgba(133, 135, 214, 0.48)",
    recentScrollbarColor: "rgba(122, 125, 207, 0.75) rgba(255,255,255,0.06)",
    recentScrollbarTrack: "rgba(255,255,255,0.04)",
    recentScrollbarThumb: "rgba(122, 125, 207, 0.75)",
    recentItemBorder: "1px solid rgba(255,255,255,0.08)",
    recentItemActiveBackground: "rgba(122, 125, 207, 0.26)",
    recentItemBackground: "rgba(255,255,255,0.04)",
    recentItemPreviewColor: "rgba(255,255,255,0.58)",
    recentEmptyColor: "rgba(255,255,255,0.5)",
    chatPaperBackground: "#1b1d24",
    chatPaperBorderLeft: "1px solid rgba(255,255,255,0.04)",
    chatPaperBackdrop: undefined,
    conversationScrollbarColor: "rgba(122, 125, 207, 0.75) rgba(255,255,255,0.04)",
    conversationScrollbarTrack: "rgba(255,255,255,0.03)",
    conversationScrollbarThumb: "rgba(122, 125, 207, 0.75)",
    messageAssistantBackground: "#2a2d38",
    messageUserBackground: "#313546",
    messageTextColor: "#f3f4f7",
    messageBorder: "1px solid rgba(255,255,255,0.06)",
    messageShadow: "none",
    messageLabelColor: "rgba(255,255,255,0.72)",
    toolbarBorderTop: "1px solid rgba(255,255,255,0.06)",
    toolbarBackground: "#20232c",
    composerBorderTop: "1px solid rgba(255,255,255,0.06)",
    composerBackground: "#20232c",
    inputLabelColor: "rgba(255,255,255,0.56)",
    inputFocusedLabelColor: "#8b8df0",
    inputBackground: "#17191f",
    inputTextColor: "#f3f4f7",
    inputBorderColor: "rgba(255,255,255,0.12)",
    inputHoverBorderColor: "rgba(139,141,240,0.52)",
    inputFocusedBorderColor: "#8b8df0",
    inputPlaceholderColor: "rgba(255,255,255,0.4)",
    sendButtonBackground: "#6264a7",
    sendButtonHoverBackground: "#787ad4",
    loaderBackground: "#2a2d38",
    loaderBorder: "1px solid rgba(255,255,255,0.06)",
    loaderColor: "#f3f4f7",
    loaderSpinnerColor: "#8b8df0",
  },
  classic: {
    pageBackground: "linear-gradient(180deg, #f3f7fb 0%, #e8eef7 48%, #dde9f5 100%)",
    sidebarBackground: "linear-gradient(180deg, rgba(230,0,0,1) 0%, rgba(163,0,0,0.96) 100%)",
    sidebarBorderRight: "1px solid rgba(255,255,255,0.10)",
    collapseButtonBackground: "transparent",
    collapseButtonBorder: "1px solid rgba(255,255,255,0.2)",
    sectionTextColor: "rgba(255,255,255,0.72)",
    collapsedTextColor: "rgba(255,255,255,0.72)",
    newChatButtonBackground: "transparent",
    newChatButtonBorder: "rgba(255,255,255,0.24)",
    newChatButtonHoverBackground: "rgba(255,255,255,0.08)",
    newChatButtonHoverBorder: "rgba(255,255,255,0.24)",
    recentScrollbarColor: "rgba(230,0,0,1) rgba(255,255,255,0.10)",
    recentScrollbarTrack: "rgba(255,255,255,0.10)",
    recentScrollbarThumb: "rgba(230,0,0,1)",
    recentItemBorder: "1px solid rgba(255,255,255,0.18)",
    recentItemActiveBackground: "rgba(230,0,0,0.22)",
    recentItemBackground: "rgba(255,255,255,0.08)",
    recentItemPreviewColor: "rgba(255,255,255,0.72)",
    recentEmptyColor: "rgba(255,255,255,0.58)",
    chatPaperBackground: "rgba(255,255,255,0.78)",
    chatPaperBorderLeft: "none",
    chatPaperBackdrop: "blur(18px)",
    conversationScrollbarColor: undefined,
    conversationScrollbarTrack: undefined,
    conversationScrollbarThumb: undefined,
    messageAssistantBackground: "#ffffff",
    messageUserBackground: "#ffffff",
    messageTextColor: "#182334",
    messageBorder: "none",
    messageShadow: "0 10px 24px rgba(15, 23, 42, 0.08)",
    messageLabelColor: undefined,
    toolbarBorderTop: "1px solid rgba(15, 23, 42, 0.08)",
    toolbarBackground: "rgba(248,250,252,0.96)",
    composerBorderTop: "1px solid rgba(15, 23, 42, 0.08)",
    composerBackground: "rgba(248,250,252,0.92)",
    inputLabelColor: undefined,
    inputFocusedLabelColor: undefined,
    inputBackground: undefined,
    inputTextColor: undefined,
    inputBorderColor: undefined,
    inputHoverBorderColor: undefined,
    inputFocusedBorderColor: undefined,
    inputPlaceholderColor: undefined,
    sendButtonBackground: "rgba(230,0,0,1)",
    sendButtonHoverBackground: "rgba(163,0,0,1)",
    loaderBackground: "#ffffff",
    loaderBorder: "none",
    loaderColor: undefined,
    loaderSpinnerColor: undefined,
  },
};

function AIMode() {
  const nextChatIdRef = useRef(2);
  const [chats, setChats] = useState([createChatSession(1)]);
  const [activeChatId, setActiveChatId] = useState(1);
  const [draft, setDraft] = useState("");
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [attachmentError, setAttachmentError] = useState("");
  const [isWorkspaceOpen, setIsWorkspaceOpen] = useState(false);
  const [isTeamsTheme, setIsTeamsTheme] = useState(() => {
    try {
      const saved = localStorage.getItem("aiModeTheme");
      return saved ? JSON.parse(saved) : false;
    } catch {
      return false;
    }
  });
  const conversationEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const uiTheme = isTeamsTheme ? themeModes.teams : themeModes.classic;

  useLayoutEffect(() => {
    localStorage.setItem("aiModeTheme", JSON.stringify(isTeamsTheme));
  }, [isTeamsTheme]);

  const activeChat = useMemo(
    () => chats.find((chat) => chat.id === activeChatId) ?? chats[0],
    [activeChatId, chats]
  );
  const recentChats = useMemo(() => [...chats].reverse(), [chats]);
  const messages = activeChat?.messages ?? initialMessages;
  const currentAttachments = activeChat?.attachments ?? [];
  const isSending = activeChat?.isSending ?? false;
  const error = activeChat?.error ?? "";

  const updateChat = (chatId, updater) => {
    setChats((currentChats) =>
      currentChats.map((chat) =>
        chat.id === chatId ? updater(chat) : chat
      )
    );
  };

  const scrollToBottom = () => {
    conversationEndRef.current?.scrollIntoView({ block: "end" });
  };

  const lastMessageContent = messages[messages.length - 1]?.content ?? "";

  useLayoutEffect(() => {
    scrollToBottom();
  }, [activeChatId, messages.length, isSending, lastMessageContent]);

  useEffect(() => {
    setPendingAttachments(currentAttachments);
  }, [currentAttachments]);

  const focusInput = () => {
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  };

  const handlePaperclipClick = () => {
    fileInputRef.current?.click();
  };

  const formatFileSize = (sizeInBytes) => {
    if (sizeInBytes < 1024) {
      return `${sizeInBytes} B`;
    }

    const sizeInKilobytes = sizeInBytes / 1024;
    if (sizeInKilobytes < 1024) {
      return `${sizeInKilobytes.toFixed(1)} KB`;
    }

    return `${(sizeInKilobytes / 1024).toFixed(1)} MB`;
  };

  const formatFileType = (file) => {
    const extension = file.name.split(".").pop()?.toUpperCase();
    return extension ? `${extension} file` : "Unknown format";
  };

  const handleAttachmentChange = (event) => {
    const files = Array.from(event.target.files ?? []);
    const MAX_FILES = 4;
    const MAX_FILE_SIZE = 2097152; // 2 MB in bytes

    if (files.length > 0) {
      if (currentAttachments.length >= MAX_FILES) {
        setAttachmentError(`You already have ${MAX_FILES} files attached. Remove one before adding more.`);
        event.target.value = "";
        return;
      }

      // Check file sizes first
      const oversizedFiles = files.filter((file) => file.size > MAX_FILE_SIZE);
      if (oversizedFiles.length > 0) {
        setAttachmentError(
          `File size limit is 2 MB. The following files exceed the limit: ${oversizedFiles.map((f) => f.name).join(", ")}`
        );
        event.target.value = "";
        return;
      }

      const nextAttachments = [...currentAttachments, ...files];
      const uniqueAttachments = new Map(
        nextAttachments.map((file) => [`${file.name}:${file.size}:${file.lastModified}`, file])
      );
      const uniqueArray = Array.from(uniqueAttachments.values());

      if (uniqueArray.length > MAX_FILES) {
        setAttachmentError(`You can only attach up to ${MAX_FILES} files. You tried to add ${uniqueArray.length} files.`);
        event.target.value = "";
        return;
      }

      setAttachmentError("");
      setPendingAttachments(uniqueArray);
      updateChat(activeChatId, (chat) => ({
        ...chat,
        attachments: uniqueArray,
      }));
    }

    event.target.value = "";
  };

  const handleRemoveAttachment = (attachmentKey) => {
    const nextAttachments = currentAttachments.filter((file) => `${file.name}:${file.size}:${file.lastModified}` !== attachmentKey);
    setPendingAttachments(nextAttachments);
    setAttachmentError("");
    updateChat(activeChatId, (chat) => ({
      ...chat,
      attachments: nextAttachments,
    }));
  };

  const handleNewChat = () => {
    const nextChatId = nextChatIdRef.current;
    nextChatIdRef.current += 1;
    setChats((currentChats) => [createChatSession(nextChatId), ...currentChats]);
    setActiveChatId(nextChatId);
    setDraft("");
    setPendingAttachments([]);
    setAttachmentError("");
    focusInput();
  };

  const handleSelectChat = (chatId) => {
    setActiveChatId(chatId);
    setDraft("");
    setAttachmentError("");
    focusInput();
  };

  const handleSend = async (prefilledMessage) => {
    const nextMessage = (prefilledMessage ?? draft).trim();
    if ((!nextMessage && pendingAttachments.length === 0) || !activeChat || activeChat.isSending) {
      return;
    }

    const targetChatId = activeChat.id;
    const nextUserMessage = {
      role: "user",
      content: nextMessage || `Attached ${pendingAttachments.length} file${pendingAttachments.length > 1 ? "s" : ""}`,
    };
    const nextHistory = [...activeChat.messages, nextUserMessage].map(({ role, content }) => ({
      role,
      content,
    }));
    const nextTitle =
      activeChat.title === "New chat"
        ? nextMessage.slice(0, 48)
        : activeChat.title;

    setDraft("");
    updateChat(targetChatId, (chat) => ({
      ...chat,
      title: nextTitle,
      error: "",
      isSending: true,
      messages: [...chat.messages, nextUserMessage, { role: "assistant", content: "" }],
    }));
    focusInput();

    try {
      const fullReply = await sendAIAgentMessage({
        message: nextMessage,
        history: nextHistory,
        attachments: pendingAttachments,
        onChunk: (accumulated) => {
          updateChat(targetChatId, (chat) => {
            const msgs = [...chat.messages];
            msgs[msgs.length - 1] = { role: "assistant", content: accumulated };
            return { ...chat, messages: msgs };
          });
        },
      });

      updateChat(targetChatId, (chat) => {
        const msgs = [...chat.messages];
        msgs[msgs.length - 1] = { role: "assistant", content: fullReply };
        return { ...chat, isSending: false, messages: msgs };
      });
    } catch (requestError) {
      updateChat(targetChatId, (chat) => ({
        ...chat,
        isSending: false,
        error: requestError.message,
        messages: [
          ...chat.messages,
          {
            role: "assistant",
            content: requestError.message || "The agent request did not complete.",
          },
        ],
      }));
    } finally {
      focusInput();
    }
  };

  return (
    <BasePageLayout
      sidebarItems={[]}
      containerMaxWidth={false}
      contentSx={{
        pt: 8,
        pb: 6,
        px: 0,
        height: "100dvh",
        boxSizing: "border-box",
        background: uiTheme.pageBackground,
        overflow: "hidden",
      }}
      containerSx={{
        width: "100%",
        maxWidth: "none",
        height: "100%",
        minHeight: "100%",
        p: 0,
      }}
    >
      <BaseBox
        sx={{
          width: "100%",
          height: "100%",
          minHeight: 0,
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            lg: isWorkspaceOpen ? "280px minmax(0, 1fr)" : "56px minmax(0, 1fr)",
            xl: isWorkspaceOpen ? "320px minmax(0, 1fr)" : "56px minmax(0, 1fr)",
          },
          gap: 0,
          overflow: "hidden",
          transition: "grid-template-columns 0.2s ease",
        }}
      >
        <BasePaper
          sx={{
            height: "100%",
            minHeight: 0,
            alignItems: "stretch",
            background: uiTheme.sidebarBackground,
            color: "#fff",
            p: { xs: 2, md: 3 },
            borderRadius: 0,
            boxShadow: "none",
            borderRight: uiTheme.sidebarBorderRight,
            overflow: "hidden",
            position: "relative",
          }}
        >
          <IconButton
            onClick={() => setIsWorkspaceOpen((current) => !current)}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "#fff",
              backgroundColor: uiTheme.collapseButtonBackground,
              border: uiTheme.collapseButtonBorder,
              borderRadius: 0,
              zIndex: 1,
            }}
          >
            {isWorkspaceOpen ? <ChevronLeftRoundedIcon /> : <ChevronRightRoundedIcon />}
          </IconButton>

          {isWorkspaceOpen ? (
            <>
              <Stack spacing={3} direction="column" sx={{ flex: 1, minHeight: 0, alignItems: "center", justifyContent: "center" }}>
                <Box>
                  <BaseTypography sx={{ fontSize: "1.5rem", fontWeight: 700, color: "#fff" }}>
                    AI Mode
                  </BaseTypography>
                </Box>

                <Box sx={{ width: 240, maxWidth: "100%", display: "flex", justifyContent: "flex-end" }}>
                  <Box
                    component="button"
                    type="button"
                    aria-label={isTeamsTheme ? "Switch to classic theme" : "Switch to Teams dark theme"}
                    onClick={() => setIsTeamsTheme((current) => !current)}
                    sx={{
                      position: "relative",
                      width: 74,
                      height: 36,
                      p: 0,
                      border: "1px solid rgba(255,255,255,0.18)",
                      borderRadius: "999px",
                      backgroundColor: isTeamsTheme ? "rgba(98, 100, 167, 0.24)" : "rgba(255, 235, 235, 0.96)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      boxShadow: isTeamsTheme
                        ? "inset 0 1px 2px rgba(255,255,255,0.08)"
                        : "inset 0 1px 2px rgba(230,0,0,0.16)",
                      transition: "background-color 0.2s ease, border-color 0.2s ease",
                      '&:hover': {
                        backgroundColor: isTeamsTheme ? "rgba(98, 100, 167, 0.32)" : "rgba(255, 225, 225, 1)",
                      },
                      '&:focus-visible': {
                        outline: "2px solid rgba(255,255,255,0.8)",
                        outlineOffset: 2,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: 3,
                        left: isTeamsTheme ? 37 : 3,
                        width: 30,
                        height: 30,
                        borderRadius: "50%",
                        backgroundColor: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: isTeamsTheme ? "#6264a7" : "#e60000",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                        transition: "left 0.22s ease, color 0.2s ease",
                      }}
                    >
                      {isTeamsTheme ? (
                        <DarkModeRoundedIcon sx={{ fontSize: 16 }} />
                      ) : (
                        <LightModeRoundedIcon sx={{ fontSize: 16 }} />
                      )}
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ width: 240, maxWidth: "100%", mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: uiTheme.sectionTextColor }}>
                    New chat
                  </Typography>
                  <BaseButton
                    variant="outlined"
                    onClick={handleNewChat}
                    sx={{
                      width: "100%",
                      justifyContent: "flex-start",
                      color: "#fff",
                      backgroundColor: uiTheme.newChatButtonBackground,
                      borderColor: uiTheme.newChatButtonBorder,
                      borderRadius: 0,
                      '&:hover': {
                        backgroundColor: uiTheme.newChatButtonHoverBackground,
                        borderColor: uiTheme.newChatButtonHoverBorder,
                      },
                    }}
                  >
                    Start a new conversation
                  </BaseButton>
                </Box>

                <Box sx={{ width: 240, maxWidth: "100%", flex: '0 1 85%', minHeight: 0, display: "flex", flexDirection: "column" }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: uiTheme.sectionTextColor }}>
                    Recent
                  </Typography>
                  <BaseBox sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                    <Stack
                      spacing={1}
                      sx={{
                        flex: 1,
                        minHeight: 0,
                        overflowY: "auto",
                        pr: 0.5,
                        scrollbarWidth: "thin",
                        scrollbarColor: uiTheme.recentScrollbarColor,
                        '&::-webkit-scrollbar': {
                          width: '8px',
                          background: uiTheme.recentScrollbarTrack,
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: uiTheme.recentScrollbarThumb,
                          borderRadius: '4px',
                        },
                      }}
                    >
                      {recentChats.length > 0 ? (
                        recentChats.map((chat) => (
                          <BaseBox
                            key={chat.id}
                            onClick={() => handleSelectChat(chat.id)}
                            sx={{
                              px: 1.5,
                              py: 1.25,
                              border: uiTheme.recentItemBorder,
                              backgroundColor:
                                chat.id === activeChatId
                                  ? uiTheme.recentItemActiveBackground
                                  : uiTheme.recentItemBackground,
                              cursor: "pointer",
                              flexShrink: 0,
                            }}
                          >
                            <BaseTypography
                              sx={{
                                color: "#fff",
                                fontWeight: 600,
                                fontSize: "0.9rem",
                                mb: 0.5,
                                lineHeight: 1.5,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                              }}
                            >
                              {chat.title}
                            </BaseTypography>
                            <BaseTypography
                              sx={{
                                color: uiTheme.recentItemPreviewColor,
                                fontSize: "0.78rem",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {chat.messages[chat.messages.length - 1]?.content ?? "No messages yet."}
                            </BaseTypography>
                          </BaseBox>
                        ))
                      ) : (
                        <BaseTypography sx={{ color: uiTheme.recentEmptyColor, fontSize: "0.85rem" }}>
                          No chats yet.
                        </BaseTypography>
                      )}
                    </Stack>
                  </BaseBox>
                </Box>
              </Stack>
            </>
          ) : (
            <BaseBox
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                pt: 7,
              }}
            >
              <BaseTypography
                sx={{
                  color: uiTheme.collapsedTextColor,
                  writingMode: "vertical-rl",
                  transform: "rotate(180deg)",
                  letterSpacing: "0.14em",
                  fontSize: "0.72rem",
                }}
              >
                AI MODE
              </BaseTypography>
            </BaseBox>
          )}
        </BasePaper>

        <BasePaper
          sx={{
            height: "100%",
            minHeight: 0,
            p: 0,
            alignItems: "stretch",
            overflow: "hidden",
            borderRadius: 0,
            boxShadow: "none",
            backgroundColor: uiTheme.chatPaperBackground,
            borderLeft: uiTheme.chatPaperBorderLeft,
            backdropFilter: uiTheme.chatPaperBackdrop,
          }}
        >
          <BaseBox
            sx={{
              flex: 1,
              overflowY: "auto",
              px: { xs: 2, md: 4 },
              py: 3,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              minHeight: 0,
              ...(isTeamsTheme
                ? {
                    scrollbarWidth: "thin",
                    scrollbarColor: uiTheme.conversationScrollbarColor,
                    '&::-webkit-scrollbar': {
                      width: '10px',
                      background: uiTheme.conversationScrollbarTrack,
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: uiTheme.conversationScrollbarThumb,
                      borderRadius: '999px',
                    },
                  }
                : {}),
            }}
          >
            {messages.map((message, index) => {
              const isAssistant = message.role === "assistant";
              return (
                <Box
                  key={`${message.role}-${index}`}
                  sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: { xs: "100%", md: "78%" },
                      px: 2,
                      py: 1.5,
                      borderRadius: 0,
                      backgroundColor: isAssistant ? uiTheme.messageAssistantBackground : uiTheme.messageUserBackground,
                      color: uiTheme.messageTextColor,
                      border: uiTheme.messageBorder,
                      boxShadow: uiTheme.messageShadow,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                      {isAssistant ? (
                        <SmartToyRoundedIcon fontSize="small" />
                      ) : (
                        <AutoAwesomeRoundedIcon fontSize="small" />
                      )}
                      <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: "0.06em", color: uiTheme.messageLabelColor }}>
                        {isAssistant ? "AGENT" : "YOU"}
                      </Typography>
                    </Box>
                    {isAssistant ? (
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          p: ({ children }) => (
                            <Typography sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6, color: uiTheme.messageTextColor, mb: 1 }}>
                              {children}
                            </Typography>
                          ),
                          code: ({ inline, className, children, ...props }) =>
                            inline ? (
                              <code
                                style={{
                                  backgroundColor: isTeamsTheme ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
                                  padding: "2px 5px",
                                  borderRadius: 3,
                                  fontSize: "0.9em",
                                }}
                                {...props}
                              >
                                {children}
                              </code>
                            ) : (
                              <pre
                                style={{
                                  backgroundColor: isTeamsTheme ? "#17191f" : "#f5f5f5",
                                  color: isTeamsTheme ? "#f3f4f7" : "#333",
                                  padding: 12,
                                  borderRadius: 4,
                                  overflowX: "auto",
                                  fontSize: "0.85em",
                                  lineHeight: 1.5,
                                }}
                              >
                                <code {...props}>{children}</code>
                              </pre>
                            ),
                        }}
                      >
                        {message.content}
                      </ReactMarkdown>
                    ) : (
                      <Typography sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6, color: uiTheme.messageTextColor }}>
                        {message.content}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
            {isSending && (
              <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderRadius: 0,
                    backgroundColor: uiTheme.loaderBackground,
                    border: uiTheme.loaderBorder,
                    color: uiTheme.loaderColor,
                    boxShadow: uiTheme.messageShadow,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={18} sx={{ color: uiTheme.loaderSpinnerColor }} />
                    <Typography variant="body2" sx={{ color: uiTheme.loaderColor }}>Agent is working...</Typography>
                  </Stack>
                </Box>
              </Box>
            )}
            <Box ref={conversationEndRef} sx={{ height: 1 }} />
          </BaseBox>

          <BaseBox
            sx={{
              borderTop: uiTheme.toolbarBorderTop,
              px: { xs: 2, md: 3 },
              py: 0.5,
              backgroundColor: uiTheme.toolbarBackground,
              boxSizing: "border-box",
              width: "100%",
              minHeight: 28,
              display: "flex",
              alignItems: "center",
              position: "relative",
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              onChange={handleAttachmentChange}
            />
            {(error || attachmentError || pendingAttachments.length > 0) && (
              <BaseBox
                sx={{
                  position: "absolute",
                  left: { xs: 16, md: 24 },
                  right: { xs: 16, md: 24 },
                  bottom: "100%",
                  mb: 1,
                  zIndex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.75,
                }}
              >
                {error && (
                  <Alert severity="error">
                    {error}
                  </Alert>
                )}

                {attachmentError && (
                  <Alert severity="error">
                    {attachmentError}
                  </Alert>
                )}

                {pendingAttachments.length > 0 && (
                  <BaseBox
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1,
                      alignItems: "center",
                    }}
                  >
                    {pendingAttachments.map((attachment) => {
                      const attachmentKey = `${attachment.name}:${attachment.size}:${attachment.lastModified}`;
                      return (
                        <BaseBox
                          key={attachmentKey}
                          sx={{
                            display: "inline-flex",
                            alignItems: "flex-start",
                            gap: 0.75,
                            px: 1,
                            py: 0.5,
                            borderRadius: 0,
                            backgroundColor: isTeamsTheme ? "rgba(255,255,255,0.08)" : "rgba(15, 23, 42, 0.06)",
                            border: isTeamsTheme ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(15, 23, 42, 0.1)",
                            color: isTeamsTheme ? "#ffffff" : "#000000",
                            maxWidth: 180,
                            flexShrink: 0,
                          }}
                        >
                          <BaseBox
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              minWidth: 0,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                maxWidth: 120,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                color: "inherit",
                                lineHeight: 1.2,
                              }}
                            >
                              {attachment.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "inherit",
                                opacity: 0.72,
                                lineHeight: 1.2,
                              }}
                            >
                              {formatFileSize(attachment.size)}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "inherit",
                                opacity: 0.72,
                                lineHeight: 1.2,
                              }}
                            >
                              {formatFileType(attachment)}
                            </Typography>
                          </BaseBox>
                          <IconButton
                            aria-label={`Remove ${attachment.name}`}
                            size="small"
                            onClick={() => handleRemoveAttachment(attachmentKey)}
                            sx={{
                              color: "inherit",
                              p: 0,
                            }}
                          >
                            <CloseRoundedIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </BaseBox>
                      );
                    })}
                  </BaseBox>
                )}
              </BaseBox>
            )}

            <IconButton
              aria-label="Attach file"
              size="small"
              onClick={handlePaperclipClick}
              sx={{
                color: isTeamsTheme ? "#ffffff" : "#000000",
                p: 0.25,
              }}
            >
              <AttachFileRoundedIcon fontSize="small" />
            </IconButton>
          </BaseBox>

          <BaseBox
            sx={{
              borderTop: uiTheme.composerBorderTop,
              px: { xs: 2, md: 3 },
              py: 2,
              backgroundColor: uiTheme.composerBackground,
            }}
          >
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "stretch", md: "flex-end" }}>
              <BaseTextField
                inputRef={inputRef}
                multiline
                minRows={3}
                maxRows={8}
                label="Ask the agent to do something"
                placeholder="Example: Create a release checklist for production deployment."
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    handleSend();
                  }
                }}
                disabled={isSending}
                sx={{
                  m: 0,
                  ...(isTeamsTheme
                    ? {
                        '& .MuiInputLabel-root': {
                          color: uiTheme.inputLabelColor,
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: uiTheme.inputFocusedLabelColor,
                        },
                      }
                    : {}),
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 0,
                    ...(isTeamsTheme
                      ? {
                          color: uiTheme.inputTextColor,
                          backgroundColor: uiTheme.inputBackground,
                          '& fieldset': {
                            borderColor: uiTheme.inputBorderColor,
                          },
                          '&:hover fieldset': {
                            borderColor: uiTheme.inputHoverBorderColor,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: uiTheme.inputFocusedBorderColor,
                          },
                          '& textarea::placeholder': {
                            color: uiTheme.inputPlaceholderColor,
                            opacity: 1,
                          },
                        }
                      : {}),
                  },
                }}
              />
              <BaseButton
                variant="contained"
                onClick={() => handleSend()}
                disabled={isSending || !draft.trim()}
                endIcon={<SendRoundedIcon />}
                sx={{
                  minWidth: { xs: "100%", md: "96px" },
                  width: { xs: "100%", md: "96px" },
                  height: { md: 56 },
                  px: 1.5,
                  borderRadius: 0,
                  ...(isTeamsTheme
                    ? {
                        backgroundColor: uiTheme.sendButtonBackground,
                        '&:hover': {
                          backgroundColor: uiTheme.sendButtonHoverBackground,
                        },
                      }
                    : {}),
                }}
              >
                Send
              </BaseButton>
            </Stack>
          </BaseBox>
        </BasePaper>
      </BaseBox>
    </BasePageLayout>
  );
}

export default AIMode;